import { FileDownload, Search } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputAdornment,
  Paper,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import {
  AssessmentStudentRow,
  Registration,
} from '../../types/AssessmentTable.type';
import { getAssessmentStudents } from '../../utils/assessmentApi';
import {
  assessmentMonthColors,
  levelAlternativeChipColors,
  levelChipColors,
} from '../../utils/colorHandler';
import { exportAssessmentProgressToXlsx } from '../../utils/xlsxExport';
import { notifyFailure, notifySuccess } from '../../utils/toast';
import ColorChip from '../common/ColorChip';
import {
  MANAGEMENT_TABLE_HEADER_COLOR,
  ManagementTableHeaderCell,
} from '../common/ManagementTable';
import { TableNoData } from '../common/TableEmptyState';
import TableLoader from '../common/TableLoader';

type Props = {
  program: 'SOF' | 'FLYING FISH';
  refreshKey: number;
  handleOpenLevelModal: (
    subsubtypeId: number,
    registration: Registration,
  ) => void;
};

const monthFor = (value: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Bangkok',
    month: 'short',
  })
    .format(date)
    .toUpperCase();
};

const display = (value: string | null | undefined) => value?.trim() || '—';

const levelPalette = {
  ...levelChipColors,
  ...levelAlternativeChipColors,
};

const asRegistration = (row: AssessmentStudentRow): Registration =>
  ({
    id: row.registrationId,
    userId: row.studentId,
    user: {
      id: row.studentId,
      firstName: row.fullName,
      lastName: '',
      preferredName: row.preferredName,
      dob: row.dob,
    },
    subsubtypeId: row.subsubtypeId,
    subsubtype: { id: row.subsubtypeId, name: row.packageName },
    duration: 1,
    price: 0,
    sos: '',
    eos: '',
    note: '',
    subtype: row.program,
    serviceType: 'COURSE',
    ticket: '',
    visitType: 'count',
    visitBalance: 0,
    createdAt: '',
    updateAt: '',
    deletionMark: false,
    courseLevel: row.level || '',
    attendancesAll: [],
    attendancesToday: [],
  }) as unknown as Registration;

const AssessmentStudentTable = ({
  program,
  refreshKey,
  handleOpenLevelModal,
}: Props) => {
  const [rows, setRows] = useState<AssessmentStudentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [levelOptions, setLevelOptions] = useState<string[]>([]);
  const [exporting, setExporting] = useState(false);

  const fetchRows = async () => {
    try {
      setLoading(true);
      const response = await getAssessmentStudents(
        program,
        page,
        limit,
        search,
        levelFilter,
      );
      setRows(response.data);
      setTotalItems(response.metadata.totalItems);
      setLevelOptions(response.metadata.levelOptions || []);
    } catch {
      notifyFailure();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchRows();
  }, [program, page, limit, levelFilter, refreshKey]);

  const fetchAllRows = async (searchValue: string, levelValue: string) => {
    const exportLimit = 100;
    const allRows: AssessmentStudentRow[] = [];
    let exportPage = 1;
    let totalPages = 1;

    do {
      const response = await getAssessmentStudents(
        program,
        exportPage,
        exportLimit,
        searchValue,
        levelValue,
      );
      allRows.push(...response.data);
      totalPages = response.metadata.totalPages || 1;
      exportPage += 1;
    } while (exportPage <= totalPages);

    return allRows;
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const hasFilter = Boolean(search.trim() || levelFilter);
      const exportRows = await fetchAllRows(
        hasFilter ? search : '',
        hasFilter ? levelFilter : '',
      );

      if (exportRows.length === 0) {
        notifyFailure();
        return;
      }

      exportAssessmentProgressToXlsx(
        exportRows,
        program,
        `assessment-${program.toLowerCase().replace(/\s+/g, '-')}-${
          hasFilter ? 'filtered' : 'all'
        }-${dayjs().format('YYYYMMDD-HHmmss')}.xlsx`,
      );
      notifySuccess();
    } catch {
      notifyFailure();
    } finally {
      setExporting(false);
    }
  };

  return (
    <Paper>
      <Box p={2} display="flex" alignItems="center" flexWrap="wrap" gap={1}>
        <TextField
          size="small"
          value={search}
          placeholder="Search by name or ID"
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') void fetchRows();
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select
            displayEmpty
            value={levelFilter}
            onChange={(event) => {
              setLevelFilter(event.target.value);
              setPage(1);
            }}
            inputProps={{ 'aria-label': 'Filter by level' }}
          >
            <MenuItem value="">All levels</MenuItem>
            {levelOptions.map((level) => (
              <MenuItem key={level} value={level}>
                {level}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          size="small"
          variant="contained"
          startIcon={<FileDownload />}
          disabled={exporting}
          onClick={() => void handleExport()}
          title={
            search.trim() || levelFilter
              ? 'Export filtered data'
              : 'Export all data'
          }
        >
          Export
        </Button>
      </Box>
      <TableContainer sx={{ maxHeight: '55vh', overflowX: 'auto' }}>
        <Table size="small" stickyHeader sx={{ minWidth: 840 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: MANAGEMENT_TABLE_HEADER_COLOR }}>
              <ManagementTableHeaderCell>Student ID</ManagementTableHeaderCell>
              <ManagementTableHeaderCell>Full name</ManagementTableHeaderCell>
              <ManagementTableHeaderCell>Age</ManagementTableHeaderCell>
              <ManagementTableHeaderCell>Last update</ManagementTableHeaderCell>
              <ManagementTableHeaderCell>Level</ManagementTableHeaderCell>
              <ManagementTableHeaderCell>
                Preferred name
              </ManagementTableHeaderCell>
              <ManagementTableHeaderCell>Program</ManagementTableHeaderCell>
              <ManagementTableHeaderCell>Action</ManagementTableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableLoader colSpan={8} />
            ) : rows.length === 0 ? (
              <TableNoData />
            ) : (
              rows.map((row) => {
                const month = monthFor(row.lastLevelUpdatedAt);
                const fullDate = row.lastLevelUpdatedAt
                  ? new Date(row.lastLevelUpdatedAt).toLocaleString('en-GB', {
                      timeZone: 'Asia/Bangkok',
                    })
                  : 'No level update';
                return (
                  <TableRow key={row.registrationId} hover>
                    <TableCell>{row.studentId}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {row.fullName}
                    </TableCell>
                    <TableCell>{row.age}</TableCell>
                    <TableCell>
                      {month ? (
                        <Tooltip
                          title={`${fullDate}${row.lastLevelUpdatedBy ? ` · ${row.lastLevelUpdatedBy}` : ''}`}
                        >
                          <Chip
                            size="small"
                            label={month}
                            aria-label={`Last update ${fullDate}`}
                            sx={{
                              color: '#fff',
                              backgroundColor: assessmentMonthColors[month],
                              fontWeight: 700,
                            }}
                          />
                        </Tooltip>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      <ColorChip
                        label={
                          row.level?.trim().toLowerCase() === 'none'
                            ? null
                            : row.level
                        }
                        palette={levelPalette}
                        emptyLabel="None"
                      />
                    </TableCell>
                    <TableCell>{display(row.preferredName)}</TableCell>
                    <TableCell>{row.program}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() =>
                          handleOpenLevelModal(
                            row.subsubtypeId,
                            asRegistration(row),
                          )
                        }
                      >
                        View / edit
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalItems}
        page={page - 1}
        rowsPerPage={limit}
        onPageChange={(_, nextPage) => setPage(nextPage + 1)}
        onRowsPerPageChange={(event) => {
          setLimit(Number(event.target.value));
          setPage(1);
        }}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
      />
    </Paper>
  );
};

export default AssessmentStudentTable;
